import React, { useState } from "react";
import { C, font, badge, btnS, lbl as lblStyle, inp as inpStyle } from "../../../../lib/styles.js";
import {
  SESSION_TYPES, SESSION_TYPE_LABELS, SESSION_TYPE_COLORS, NON_CAPTURE_SESSION_TYPES,
} from "../../../../lib/constants.js";
import {
  fmtTime, fmtDate, FMT_DATE_DOW, addressMapQuery,
  sessionHasCaptureData, validateSessionAdd, studioPatch, clearLocationPatch,
} from "../../../../lib/utils.js";
import LocationInputs from "./LocationInputs.jsx";

const STC = SESSION_TYPE_COLORS(C);

const SETUP_NOTE_PLACEHOLDER = {
  flight: "e.g. LGA → LAX, flight 1742, crew of 3",
  setup: "e.g. transport rig, partial setup, pack equipment case",
  strike: "e.g. teardown, pack out, equipment return",
};

export default function SessionEditor({
  job, session, canEdit, canManageSessions,
  onWriteField, onWriteFields, onClose,
}) {
  const [convertError, setConvertError] = useState("");
  const [pendingStudio, setPendingStudio] = useState(false);

  const hasCaptureData = sessionHasCaptureData(session);
  const captureCount = (session?.captureLog || []).length;
  const hiResCount = (session?.captureLog || []).filter(r => r?.hiResRequested).length;
  const dateDisp = fmtDate(session.date, FMT_DATE_DOW) || "—";
  const mapQuery = addressMapQuery(session);

  const convertType = (t) => {
    if (t === session.sessionType) return;
    if (hasCaptureData) return;
    const check = validateSessionAdd(
      { sessions: (job.sessions || []).filter(x => x?.id !== session.id) },
      { ...session, sessionType: t },
    );
    if (!check.ok) { setConvertError(check.reason); return; }
    setConvertError("");
    onWriteFields({ sessionType: t });
  };

  const applyStudio = (goStudio) => onWriteFields(goStudio ? studioPatch() : clearLocationPatch());

  const requestStudio = (goStudio) => {
    if (goStudio && (session.locationVenue || session.location || session.locationAddress)) {
      setPendingStudio(true);
      return;
    }
    applyStudio(goStudio);
  };

  const isNonCapture = NON_CAPTURE_SESSION_TYPES.includes(session.sessionType);

  return (
    <div style={S.editor}>
      <div style={S.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={badge(STC[session.sessionType] || C.muted)}>{SESSION_TYPE_LABELS[session.sessionType] || session.sessionType}</span>
          {canEdit ? (
            <input
              type="date"
              style={{ ...inpStyle, padding: "5px 8px", fontSize: 13, width: "auto", background: C.surface }}
              value={session.date || ""}
              onChange={e => onWriteField("date", e.target.value)}
            />
          ) : (
            <span style={{ fontSize: 13, color: C.text }}>{dateDisp}</span>
          )}
        </div>
        <button style={{ ...btnS, padding: "4px 10px", fontSize: 10 }} onClick={onClose}>Back to sessions</button>
      </div>

      {canManageSessions && (
        <div style={S.typeConverter}>
          <div>
            <div style={{ ...lblStyle, fontSize: 9, marginBottom: 4 }}>Type</div>
            <div style={S.segCtl}>
              {SESSION_TYPES.map(t => {
                const active = session.sessionType === t;
                const blocked = !active && hasCaptureData;
                return (
                  <button
                    key={t}
                    disabled={!canEdit || blocked || active}
                    title={blocked ? "Clear capture data first" : ""}
                    onClick={() => convertType(t)}
                    style={{
                      padding: "4px 10px", borderRadius: 4,
                      fontSize: 11, fontFamily: font, fontWeight: active ? 500 : 400,
                      border: "none",
                      cursor: (blocked || !canEdit) ? "not-allowed" : (active ? "default" : "pointer"),
                      background: active ? C.border : "transparent",
                      color: active ? C.text : (blocked ? `${C.muted}80` : C.muted),
                      opacity: blocked ? 0.5 : 1,
                    }}
                  >{SESSION_TYPE_LABELS[t]}</button>
                );
              })}
            </div>
          </div>
          <div style={{ fontSize: 11, color: hasCaptureData ? C.danger : C.muted, maxWidth: 320 }}>
            {hasCaptureData
              ? `${captureCount} capture item${captureCount === 1 ? "" : "s"} on this session. Clear them to convert type.`
              : "No capture data on this session. Convert freely."}
          </div>
        </div>
      )}
      {convertError && <div style={{ marginTop: -6, marginBottom: 12, fontSize: 11, color: C.danger }}>{convertError}</div>}

      <div className="jc-time2" style={S.grid2}>
        <div>
          <label style={lblStyle}>Call Time</label>
          {canEdit ? (
            <input type="time" style={{ ...inpStyle, background: C.surface }} value={session.callTime || ""} onChange={e => onWriteField("callTime", e.target.value)} />
          ) : (
            <div style={S.val}>{session.callTime ? fmtTime(session.callTime) : <span style={{ color: C.muted, fontStyle: "italic" }}>TBD</span>}</div>
          )}
        </div>
        <div>
          <label style={lblStyle}>Wrap Time</label>
          {canEdit ? (
            <input type="time" style={{ ...inpStyle, background: C.surface }} value={session.wrapTime || ""} onChange={e => onWriteField("wrapTime", e.target.value)} />
          ) : (
            <div style={S.val}>{session.wrapTime ? fmtTime(session.wrapTime) : <span style={{ color: C.muted, fontStyle: "italic" }}>TBD</span>}</div>
          )}
        </div>
      </div>

      <div style={S.divider} />

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <label style={{ ...lblStyle, marginBottom: 0 }}>Location</label>
        {canManageSessions && (
          <div style={S.segCtl}>
            {[{ k: false, label: "On-location" }, { k: true, label: "Studio" }].map(opt => {
              const active = !!session.isStudio === opt.k;
              return (
                <button
                  key={opt.label}
                  disabled={!canEdit || active}
                  onClick={() => !active && canEdit && requestStudio(opt.k)}
                  style={{
                    padding: "4px 10px", borderRadius: 4, fontSize: 11, fontFamily: font,
                    fontWeight: active ? 500 : 400, border: "none",
                    cursor: (!canEdit || active) ? "default" : "pointer",
                    background: active ? C.border : "transparent",
                    color: active ? C.text : C.muted,
                  }}
                >{opt.label}</button>
              );
            })}
          </div>
        )}
        {pendingStudio && (
          <div style={S.confirmBox}>
            <span style={{ fontSize: 10.5, color: C.amber }}>Replace this session’s location with the studio address?</span>
            <button style={{ ...btnS, padding: "3px 9px", fontSize: 10, color: C.amber, borderColor: `${C.amber}60` }} onClick={() => { applyStudio(true); setPendingStudio(false); }}>Yes, use Studio</button>
            <button style={{ ...btnS, padding: "3px 9px", fontSize: 10 }} onClick={() => setPendingStudio(false)}>Cancel</button>
          </div>
        )}
      </div>

      <LocationInputs
        value={session}
        onField={onWriteField}
        disabled={!canEdit || session.isStudio}
        background={C.surface}
      />
      {!session.isStudio && mapQuery && (
        <a href={"https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(mapQuery)} target="_blank" rel="noopener noreferrer" style={S.mapLink}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          Open in Google Maps
        </a>
      )}

      {session.sessionType === "capture" && (
        <>
          <div style={S.divider} />
          <div>
            <label style={lblStyle}>Day type</label>
            <div style={{ ...S.segCtl, marginTop: 4 }}>
              {[{ k: "full", label: "Full day" }, { k: "partial", label: "Partial" }].map(opt => {
                const active = (session.dayType || "full") === opt.k;
                return (
                  <button
                    key={opt.k}
                    disabled={!canEdit || active}
                    onClick={() => !active && canEdit && onWriteField("dayType", opt.k)}
                    style={{
                      padding: "4px 10px", borderRadius: 4, fontSize: 11, fontFamily: font,
                      fontWeight: active ? 500 : 400, border: "none",
                      cursor: (!canEdit || active) ? "default" : "pointer",
                      background: active ? C.border : "transparent",
                      color: active ? C.text : C.muted,
                    }}
                  >{opt.label}</button>
                );
              })}
            </div>
          </div>
          <div style={S.captureHint}>
            Capture log, notes, hi-res requests, processing, and delivery data for this session are edited in the tabs above, scoped to <strong style={{ color: C.text, fontWeight: 400 }}>{SESSION_TYPE_LABELS[session.sessionType]} · {dateDisp}</strong>.
            {captureCount > 0 && <> {captureCount} capture item{captureCount === 1 ? "" : "s"} logged{hiResCount > 0 ? `, ${hiResCount} hi-res requested` : ""}.</>}
          </div>
        </>
      )}

      {isNonCapture && (
        <>
          <div style={S.divider} />
          <div>
            <label style={lblStyle}>Notes</label>
            {canEdit ? (
              <input
                style={{ ...inpStyle, background: C.surface }}
                value={session.notes || ""}
                onChange={e => onWriteField("notes", e.target.value)}
                placeholder={SETUP_NOTE_PLACEHOLDER[session.sessionType] || ""}
              />
            ) : (
              <div style={S.val}>{session.notes || "—"}</div>
            )}
          </div>
          <div style={S.emptyInfo}>
            <strong style={{ color: C.text, fontWeight: 400 }}>No capture on this day.</strong> {SESSION_TYPE_LABELS[session.sessionType]} sessions don{"\u2019"}t track capture logs, hi-res requests, or processing.
          </div>
        </>
      )}
    </div>
  );
}

const S = {
  editor: { marginTop: 14, padding: 14, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 6 },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10, flexWrap: "wrap" },
  typeConverter: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: 12, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6,
    padding: "10px 12px", marginBottom: 14, flexWrap: "wrap",
  },
  segCtl: {
    display: "inline-flex", background: C.surface,
    border: `1px solid ${C.border}`, borderRadius: 6, padding: 2,
  },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 },
  divider: { height: 1, background: C.border, margin: "14px 0" },
  val: { fontSize: 13, color: C.text, padding: "3px 0" },
  mapLink: {
    display: "inline-flex", alignItems: "center", gap: 6, marginTop: 4,
    padding: "4px 10px", borderRadius: 6, fontSize: 11, color: C.blue,
    background: `${C.blue}10`, border: `1px solid ${C.blue}25`, textDecoration: "none",
  },
  confirmBox: {
    display: "inline-flex", alignItems: "center", gap: 8,
    padding: "6px 10px", borderRadius: 6,
    background: `${C.amber}14`, border: `1px solid ${C.amber}40`,
    flexWrap: "wrap",
  },
  captureHint: {
    marginTop: 14, padding: "10px 14px",
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6,
    fontSize: 11, color: C.muted,
  },
  emptyInfo: {
    marginTop: 14, background: C.surface, border: `1px dashed ${C.border}`,
    borderRadius: 6, padding: "14px 16px",
    fontSize: 12, color: C.muted, textAlign: "center",
  },
};
